// Importing Hook to access URL parameters
import { useParams } from "react-router-dom";
// Importing Hooks for managing component lifecycle and state
import { useEffect, useState } from "react";
// HTTP client for making API requests
import axios from "axios";
// Importing Custom components, utilities, and constants
import {
  DataGridTable,
  authErrorRedirect,
  apiURI,
  useAuthInfo,
  Loader,
  checkKnownDateOrString,
  ApproverHistoryDisplay,
  useApproverHistoryList,
} from "../../../common";
// Importing Hook for imperative navigation
import { useNavigate } from "react-router-dom";
// Importing Material-UI components
import { Grid, Typography } from "@mui/material";
// Importing custom components
import { ResubmitRequestButton } from "../../addRequest/resubmitRequest";
import { HODApprovalButton } from "./HODApprovalButton.component";
import { HODApprovalModal } from "./HODApprovalModal.component";
import { CreateRequestDetailsTableColumns } from "./functions/createRequestDetailsTableColumns.function";
import { RequestDetailsItemDetailsDialog } from "./RequestDetailsItemDetailsDialog.component";
// Importing Utility library for JavaScript
import _ from "lodash";

export function RequestDetails() {
  // Extract ID parameter from the URL
  const { id } = useParams();
  // Extract item parameter from the URL
  const { itemID } = useParams();

  // Navigation function for redirecting
  const history = useNavigate();
  // Custom hook to get decoded authentication information
  const decoded = useAuthInfo();

  const { approverHistoryList, isApproverHistoryListLoading } =
    useApproverHistoryList(id);
  // State for managing errors
  const [errors, setErrors] = useState();
  // State for storing details data
  const [detailsData, setDetailsData] = useState();
  // State for storing list of items
  const [itemList, setItemList] = useState([]);

  // State for managing statuses
  const [statuses, setStatuses] = useState();
  // State to manage disable status of request button
  const [requestButtonDisabled, setRequestButtonDisabled] = useState(false);
  // State for selected row data
  const [selectedRowsData, setSelectedRowsData] = useState();
  // State to manage disable status of HOD approval button
  const [hodApprovalButtonDisabled, setHodApprovalButtonDisabled] =
    useState(true);
  // State for controlling visibility of HOD approval modal
  const [openHODApprovalModal, setOpenHODApprovalModal] = useState(false);

  // Function to open HOD approval modal
  const handleOpenHODApprovalModal = () => {
    setOpenHODApprovalModal(true);
  };

  // State for storing row data in dialog
  const [rowData, setRowData] = useState();
  // State for controlling visibility of item details modal
  const [openItemDetailsModal, setOpenItemDetailsModal] = useState(false);
  // Function to open item details modal
  const handleItemDetailsModalOpen = (data) => {
    setOpenItemDetailsModal(true);
    // Sets data for the item details dialog
    setRowData(data);
  };
  // Handler function for clicking on a row to open item details modal
  function handleRowClick(event) {
    handleItemDetailsModalOpen(event.row);
  }
  // Function to close item details modal
  const handleItemDetailsModalClose = () => {
    setOpenItemDetailsModal(false);
  };

  // Check if qualityReportsID is passed in parameters to open the modal directly
  useEffect(() => {
    if (itemID) {
      const rowFromParams = itemList.find(
        (item) => item.sampleItemID === itemID
      );
      handleItemDetailsModalOpen(rowFromParams);
    }
  }, [itemList, itemID]);

  useEffect(() => {
    // Fetches data from API based on ID
    axios
      .get(`${apiURI}/form/id/${id}`)
      .then((response) => {
        // Destructure relevant data from API response
        let {
          procurementApproval,
          sampleRequestStatus,
          requesterSampleImage,
          organization,
          items,
          ...dataRest
        } = response.data;
        // Sets item list state
        setItemList(items);
        // Sets remaining data details state
        setDetailsData(dataRest);
        // Sets procurement approval status
        setStatuses({
          procurementApproval,
        });
      })
      .catch((response) => {
        console.log("error in axios form call react");
        setErrors({});
        setErrors(response.response.data);
        // Redirects to error page if authentication error occurs
        authErrorRedirect(response.response.data, history);
      });

    // eslint-disable-next-line no-console
  }, [id, history]); // Runs effect when ID or history changes

  // Row Selected handler for selected rows
  function onHodApprovalRowSelectionHandler(
    itemList,
    setSelectedRowsData,
    setHodApprovalButtonDisabled
  ) {
    return (ids) => {
      if (ids) {
        let selectedRows = [];
        ids.map((id) =>
          itemList.find(
            (row) => row.id === id && selectedRows.push(row.sampleItemID)
          )
        );

        // Sets selected row data state
        setSelectedRowsData(selectedRows);

        selectedRows.length > 0
          ? setHodApprovalButtonDisabled(false)
          : setHodApprovalButtonDisabled(true); // Disables HOD approval button based on selected rows
      }
    };
  }

  function renderButton(procurementStatus) {
    switch (procurementStatus) {
      case "Accepted":
        return (
          decoded.role === "hod" &&
          // Renders HOD approval button if procurement status is Accepted and user role is HOD
          HODApprovalButton(
            detailsData.sampleRequestID,
            hodApprovalButtonDisabled,
            handleOpenHODApprovalModal
          )
        );
      case "Rejected":
        return (
          decoded.email === detailsData.email &&
          // Renders resubmit request button if request is rejected and user email matches request email
          ResubmitRequestButton(
            detailsData.sampleRequestID,
            requestButtonDisabled,
            history
          )
        );

      default:
        break;
    }
  }

  errors && console.log(errors);

  return detailsData ? ( // Renders details if detailsData is available
    <>
      <Grid
        container
        justifyContent="center"
        rowSpacing={4}
        columnSpacing={2}
        sx={{
          paddingBottom: 4,
          px: 3,
          maxWidth: "100vw",
        }}
      >
        <Grid item xs={12}>
          <div className="card">
            <div
              className="card__text"
              style={{
                padding: "20px 20px",
                overflowWrap: "break-word",
              }}
            >
              <Grid item xs={12} mb={3}>
                <Typography component="h1" variant="h6">
                  The list of items in the sample request is given below. Click
                  on the items in the list to view them in detail
                </Typography>
              </Grid>
              <Grid item xs={12}>
                {/* Renders sample request details */}
                <Typography variant="h5" gutterBottom>
                  <b>Sample Request Details</b>
                </Typography>
              </Grid>

              <Grid container columnSpacing={1}>
                {Object.keys(detailsData).map((oneKey, i) => {
                  return (
                    <Grid item xs={12} sm={6} md={4} lg={2} key={i}>
                      <Grid item>
                        <Typography
                          sx={{
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          {_.startCase(Object.keys(detailsData)[i]) + ":"}
                          <ApproverHistoryDisplay
                            item={Object.keys(detailsData)[i]}
                            data={approverHistoryList}
                            isLoading={isApproverHistoryListLoading}
                          />
                        </Typography>
                      </Grid>

                      <Grid item>
                        <Typography
                          variant="h6"
                          sx={{
                            mb: 1.5,

                            color: "black",
                          }}
                        >
                          {checkKnownDateOrString(oneKey, detailsData[oneKey])}
                        </Typography>
                      </Grid>
                    </Grid>
                  );
                })}
              </Grid>
              {/* Renders procurement approval details */}
              <Typography sx={{ mt: 3 }} variant="h5" gutterBottom>
                <b>Procurement Approval</b>
              </Typography>
              <Grid container columnSpacing={1}>
                {Object.keys(statuses.procurementApproval).map((oneKey, i) => {
                  return (
                    <Grid item xs={12} sm={6} md={4} lg={2} key={i}>
                      <Grid item>
                        <Typography>
                          {_.startCase(
                            Object.keys(statuses.procurementApproval)[i]
                          ) + ":"}
                        </Typography>
                      </Grid>

                      <Grid item>
                        <Typography
                          variant="h6"
                          sx={{
                            mb: 1.5,
                            color: "black",
                          }}
                        >
                          {checkKnownDateOrString(
                            oneKey,
                            statuses.procurementApproval[oneKey]
                          )}
                        </Typography>
                      </Grid>
                    </Grid>
                  );
                })}
              </Grid>
            </div>
          </div>
        </Grid>
        {/* Renders item list in sample request */}
        <Grid item xs={12}>
          {itemList && (
            <DataGridTable
              title="Item List"
              description="Click on a row to view item details"
              data={itemList}
              detailsBool={true}
              sampleRequestID={detailsData.sampleRequestID}
              checkBoxBool={
                decoded.role === "hod" &&
                statuses.procurementApproval.status === "Accepted" &&
                true
              }
              isRowSelectable={"hodApproval"}
              onRowsSelectionHandler={onHodApprovalRowSelectionHandler(
                itemList,
                setSelectedRowsData,
                setHodApprovalButtonDisabled
              )}
              button={renderButton(statuses.procurementApproval.status)}
              handleRowClick={handleRowClick}
              createColumns={CreateRequestDetailsTableColumns}
            />
          )}
        </Grid>
      </Grid>
      {/* Render HOD Approval modal if role=hod and button is clicked */}
      <HODApprovalModal
        open={openHODApprovalModal}
        setOpen={setOpenHODApprovalModal}
        handleOpen={handleOpenHODApprovalModal}
        selectedRowsData={selectedRowsData}
        history={history}
        detailsData={detailsData}
        itemList={itemList}
      />
      {/* Renders item details when a row is clicked */}
      {rowData && (
        <RequestDetailsItemDetailsDialog
          rowData={rowData}
          open={openItemDetailsModal}
          handleClose={handleItemDetailsModalClose}
          sampleRequestID={detailsData.sampleRequestID}
        />
      )}
    </>
  ) : errors?.norequest ? (
    <Grid
      container
      justifyContent="center"
      alignItems="center"
      marginTop="170px"
      direction="column"
    >
      {/* Renders error page if request is not found */}
      <p>
        There is no sample request for this <b>Sample Request ID</b>;
      </p>
      <p>
        Please make sure that the <b>Sample Request ID</b> is correct in the
        link you're trying to access.
      </p>
    </Grid>
  ) : (
    <Grid container justifyContent="center" marginTop="170px">
      <Loader />
    </Grid>
  );
}
